import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';

const Header = ({ onOpenAuthModal }) => {
  const { theme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
    document.body.style.overflow = '';
  };

  const handleNavClick = (e) => {
    if (e.target.id === 'openLoginSignup') {
      e.preventDefault();
      onOpenAuthModal('login');
      closeMobileMenu();
    } else if (e.target.id === 'openSignup') {
      e.preventDefault();
      onOpenAuthModal('signup');
      closeMobileMenu();
    } else {
      closeMobileMenu();
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} id="navbar">
      <div className="nav-container">
        {/* Enhanced Logo Container */}
        <a href="#home" className="logo-container" onClick={closeMobileMenu}>
          <div className="logo-images">
            <img 
              src="/assets/logo/amalika-light.jpg" 
              alt="AMALIKA WORLD" 
              className="logo-img light-logo" 
            />
            <img 
              src="/assets/logo/amalika-dark.jpg" 
              alt="AMALIKA WORLD" 
              className="logo-img dark-logo" 
            />
          </div>
          <div className="logo-text-container">
            <span className="logo-main">AMALIKA WORLD</span>
            <span className="logo-tagline">Future Ready Education</span>
          </div>
        </a>
        
        {/* Enhanced Navigation Links */}
        <ul className={`nav-links ${isMobileMenuOpen ? 'active' : ''}`} id="navLinks">
          <li>
            <a href="#home" onClick={handleNavClick} className="nav-link">
              <i className="fas fa-home"></i>
              <span>Home</span>
            </a>
          </li>
          <li>
            <a href="#about" onClick={handleNavClick} className="nav-link">
              <i className="fas fa-info-circle"></i>
              <span>About</span>
            </a>
          </li>
          <li className="dropdown-container">
            <button 
              className={`dropdown-toggle ${isDropdownOpen ? 'active' : ''}`}
              onClick={toggleDropdown}
              aria-haspopup="true" 
              aria-expanded={isDropdownOpen}
            >
              <i className="fas fa-graduation-cap"></i>
              <span>Courses</span>
              <i className="fas fa-chevron-down dropdown-arrow"></i>
            </button>
            <ul className={`dropdown-menu ${isDropdownOpen ? 'active' : ''}`}>
              <li>
                <a href="#languages" onClick={handleNavClick} className="dropdown-link">
                  <i className="fas fa-language"></i>
                  <span>Languages</span>
                </a>
              </li>
              <li>
                <a href="#robotics" onClick={handleNavClick} className="dropdown-link">
                  <i className="fas fa-robot"></i>
                  <span>Robotics</span>
                </a>
              </li>
              <li>
                <a href="#programming" onClick={handleNavClick} className="dropdown-link">
                  <i className="fas fa-code"></i>
                  <span>Programming</span>
                </a>
              </li>
              <li className="dropdown-divider"></li>
              <li>
                <a href="#" onClick={handleNavClick} className="dropdown-link">
                  <i className="fas fa-child"></i>
                  <span>Junior Kids</span>
                </a>
              </li>
              <li>
                <a href="#" onClick={handleNavClick} className="dropdown-link">
                  <i className="fas fa-user-friends"></i>
                  <span>Adults / Evening</span>
                </a>
              </li>
            </ul>
          </li>
          <li>
            <a href="#" id="openLoginSignup" onClick={handleNavClick} className="nav-link auth-link">
              <i className="fas fa-sign-in-alt"></i>
              <span>Login</span>
            </a>
          </li>
          <li>
            <a href="#" id="openSignup" onClick={handleNavClick} className="nav-link auth-link signup">
              <i className="fas fa-user-plus"></i>
              <span>Sign Up</span>
            </a>
          </li>
          <li>
            <a href="#contact" onClick={handleNavClick} className="nav-link">
              <i className="fas fa-envelope"></i>
              <span>Contact</span>
            </a>
          </li>
        </ul>

        {/* Enhanced Controls */}
        <div className="nav-controls">
          <div className="control-group">
            <button 
              className="theme-toggle" 
              onClick={toggleTheme}
              id="themeToggle" 
              aria-label="Toggle theme"
              data-tooltip={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
            >
              <div className="theme-icon-container">
                <i className="fas fa-moon theme-icon"></i>
                <i className="fas fa-sun theme-icon"></i>
              </div>
              <span className="theme-text">{theme === 'light' ? 'Dark' : 'Light'}</span>
            </button>

            <div className="lang-select-wrapper">
              <select 
                className="lang-select" 
                onChange={(e) => {
                  console.log('Language changed to:', e.target.value);
                  localStorage.setItem('language', e.target.value);
                }}
                aria-label="Select language"
              >
                <option value="en">🇺🇸 EN</option>
                <option value="ar">🇸🇦 AR</option>
                <option value="fr">🇫🇷 FR</option>
                <option value="it">🇮🇹 IT</option>
              </select>
              <i className="fas fa-chevron-down lang-arrow"></i>
            </div>
          </div>

          <div 
            className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            id="hamburger" 
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={closeMobileMenu}></div>
      )}
    </nav>
  );
};

export default Header;